import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '@lib/api/client';

// ── Client quote review (portal audit) ──────────────────────────────────────
// "Send Quote" emails used to link clients into the STAFF app; there was no
// portal page and no way to approve or decline. This is the missing half of
// the estimate → quote → invoice flow, client-side.

interface QItem { item_name: string; description: string | null; quantity: number; unit: string | null; unit_price: number; total: number }
interface QSection { id: number; name: string; subtotal: number; items: QItem[] }
interface QuoteDetail {
  id: number; quote_number: string; status: string;
  subtotal: number; tax_rate: number; tax_amount: number; total: number;
  valid_until: string | null; notes: string | null; terms: string | null;
  job_title: string | null; site_address: string | null;
  sections: QSection[];
}

const fmt = (n: unknown) => `$${(Number(n) || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string | null) => d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' }) : null;

export const ClientQuoteDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<QuoteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState<'approve' | 'decline' | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<'approved' | 'declined' | null>(null);

  useEffect(() => {
    apiClient.get(`/portal/me/quotes/${id}`)
      .then(r => setQuote(r.data.data))
      .catch(() => setError('We couldn\'t find that quote — it may have been withdrawn.'))
      .finally(() => setLoading(false));
  }, [id]);

  const respond = async (action: 'approve' | 'decline') => {
    setBusy(true);
    try {
      await apiClient.post(`/portal/me/quotes/${id}/respond`, { action, note: note.trim() || undefined });
      setDone(action === 'approve' ? 'approved' : 'declined');
      setConfirm(null);
      setQuote(q => (q ? { ...q, status: action === 'approve' ? 'approved' : 'declined' } : q));
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setError(ax.response?.data?.error || 'Could not record your response — please call us at (250) 510-0677.');
      setConfirm(null);
    } finally { setBusy(false); }
  };

  if (loading) return <div className="space-y-3 pt-2"><div className="h-40 rounded-xl bg-gray-100 animate-pulse" /><div className="h-64 rounded-xl bg-gray-100 animate-pulse" /></div>;
  if (!quote) {
    return (
      <div className="pt-8 text-center space-y-3">
        <span className="material-symbols-outlined text-gray-300 text-4xl">request_quote</span>
        <p className="text-sm text-gray-500">{error || 'Quote not found.'}</p>
        <button onClick={() => navigate('/portal/client')} className="text-sm font-semibold text-[#C77D3F]">← Back home</button>
      </div>
    );
  }

  const statusChip = quote.status === 'approved'
    ? { label: 'Approved — thank you!', cls: 'bg-green-100 text-green-800' }
    : quote.status === 'declined'
      ? { label: 'Declined', cls: 'bg-gray-100 text-gray-600' }
      : { label: 'Awaiting your decision', cls: 'bg-[#C77D3F]/15 text-[#8a5526]' };

  return (
    <div className="space-y-4 pb-6">
      <div className="flex items-center gap-2 pt-1">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-semibold text-[#C77D3F]">
          <span className="material-symbols-outlined text-base">arrow_back_ios</span> Back
        </button>
        <span className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full ${statusChip.cls}`}>{statusChip.label}</span>
      </div>

      {done && (
        <div className={`p-4 rounded-xl border text-sm flex items-start gap-2 ${done === 'approved' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
          <span className="material-symbols-outlined text-lg">{done === 'approved' ? 'celebration' : 'thumb_down'}</span>
          {done === 'approved'
            ? 'Wonderful — we\'ve been notified and we\'ll be in touch shortly to get things moving.'
            : 'No problem — we\'ve been notified. If you\'d like to talk through changes, call or message us any time.'}
        </div>
      )}
      {error && quote && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}

      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <p className="text-xs font-bold uppercase tracking-widest text-[#C77D3F]">Quote {quote.quote_number}</p>
        {quote.job_title && <p className="text-lg font-bold text-gray-900 mt-0.5">{quote.job_title}</p>}
        {quote.site_address && <p className="text-xs text-gray-500">{quote.site_address}</p>}
        {fmtDate(quote.valid_until) && (
          <p className="text-xs text-gray-400 mt-1">Valid until {fmtDate(quote.valid_until)}</p>
        )}

        <div className="mt-4 space-y-4">
          {quote.sections.map(s => (
            <div key={s.id}>
              <div className="flex items-baseline justify-between gap-2 border-b border-gray-200 pb-1">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{s.name}</p>
                <p className="text-xs font-semibold text-gray-500">{fmt(s.subtotal)}</p>
              </div>
              <ul className="divide-y divide-gray-50">
                {s.items.map((it, i) => (
                  <li key={i} className="py-2 flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <p className="text-gray-800">{it.item_name}</p>
                      {it.description && <p className="text-xs text-gray-400">{it.description}</p>}
                      <p className="text-[11px] text-gray-400">{Number(it.quantity)}{it.unit ? ` ${it.unit}` : ''} × {fmt(it.unit_price)}</p>
                    </div>
                    <span className="font-semibold text-gray-900 whitespace-nowrap">{fmt(it.total)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-4 ml-auto max-w-[240px] space-y-1 text-sm border-t border-gray-200 pt-2">
          {Number(quote.subtotal) > 0 && <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{fmt(quote.subtotal)}</span></div>}
          {Number(quote.tax_amount) > 0 && <div className="flex justify-between text-gray-600"><span>Tax ({Number(quote.tax_rate)}%)</span><span>{fmt(quote.tax_amount)}</span></div>}
          <div className="flex justify-between font-black text-[#C77D3F] text-base"><span>Total</span><span>{fmt(quote.total)}</span></div>
        </div>

        {quote.terms && <p className="mt-4 text-xs text-gray-500"><strong>Terms:</strong> {quote.terms}</p>}
        {quote.notes && <p className="mt-2 text-xs text-gray-500">{quote.notes}</p>}
      </div>

      {quote.status === 'sent' && !done && (
        <div className="space-y-2">
          <button onClick={() => setConfirm('approve')}
            className="w-full py-3.5 bg-[#C77D3F] text-white rounded-2xl text-sm font-bold active:scale-[0.99]">
            Approve this quote
          </button>
          <button onClick={() => setConfirm('decline')}
            className="w-full py-3 border border-gray-300 text-gray-600 rounded-2xl text-sm font-semibold active:scale-[0.99]">
            Decline
          </button>
          <p className="text-[11px] text-gray-400 text-center">Questions first? Call us at (250) 510-0677 — happy to walk through it.</p>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => !busy && setConfirm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900">
              {confirm === 'approve' ? `Approve quote for ${fmt(quote.total)}?` : 'Decline this quote?'}
            </h3>
            <p className="text-xs text-gray-500">
              {confirm === 'approve'
                ? 'We\'ll be notified right away and get your project moving.'
                : 'Totally fine — a quick note about why helps us come back with something better.'}
            </p>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
              placeholder={confirm === 'approve' ? 'Anything to add? (optional)' : 'What didn\'t work for you? (optional)'}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40 resize-none" />
            <div className="flex gap-2">
              <button onClick={() => setConfirm(null)} disabled={busy}
                className="flex-1 h-11 rounded-xl border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={() => respond(confirm)} disabled={busy}
                className={`flex-1 h-11 rounded-xl text-sm font-bold text-white disabled:opacity-60 ${confirm === 'approve' ? 'bg-[#C77D3F]' : 'bg-gray-500'}`}>
                {busy ? 'Sending…' : confirm === 'approve' ? 'Yes, approve' : 'Yes, decline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
